<template>
  <div class="contact-form">
    <div class="bubble-loader" v-if="loading">
      <span></span><span></span><span></span>
    </div>
    <div class="contact-content" v-if="!isSubmit">      
      <div class="header-section">
        <p class="title">{{ $t("booking.book_demo_to_start") }}</p>
        <p class="subtitle">{{ $t("booking.book_demo_to_start_desc") }}</p>
      </div>
      <el-form 
        ref="form" 
        :model="formData" 
        :rules="rules"       
        label-position="top"
        class="form-section"
        @validate="handleValidate"
      >
        <el-form-item prop="fullName" :rules="rules.fullName">
          <p class="title">
            {{ $t('booking.name') }}<span class="required">*</span>
          </p>
          <el-input
            v-model="formData.fullName"
            clearable
            size="medium"
            placeholder="John Smith"
            :aria-label="$t('booking.name')"
          ></el-input>
        </el-form-item>
        <el-form-item prop="companyName" :rules="rules.companyName">
          <p class="title">
            {{ $t('booking.your_company_name') }}<span class="required">*</span>
          </p>
          <el-input
            v-model="formData.companyName"
            clearable size="medium"
            placeholder="John Smith Agency"
            :aria-label="$t('booking.your_company_name')"
          ></el-input>
        </el-form-item>
        <el-form-item prop="email" :rules="rules.email">
          <p class="title">
            {{ $t('booking.your_email') }}<span class="required">*</span>
          </p>
          <el-input
            v-model="formData.email"
            clearable
            size="medium"
            placeholder="john@gmail.com"
            :aria-label="$t('booking.your_email')"
          ></el-input>
        </el-form-item>
        <el-form-item prop="mobile" :rules="rules.mobile">        
          <p class="title">
            {{ $t('booking.mobile') }}<span class="required">*</span>
          </p>
          <div class="item-row">
            <el-select v-model="formData.areacode" class="custom-select" filterable>
              <el-option
                v-for="item in areaCodeList"
                :key="item.code"
                :label="item.areacode"
                :value="item.areacode"
                >              
                <p>{{ item.areacode }} {{ item.country }}</p>
              </el-option>
            </el-select>
            <el-input
              v-model="formData.mobile"
              clearable
              size="medium"
              placeholder=""
              :aria-label="$t('booking.mobile')"
            ></el-input>
          </div>
        </el-form-item>
        <el-form-item prop="interests" :rules="rules.interests">
          <p class="title subtitle">
            <span>{{ $t("booking.interestin") }}<span class="required">*</span></span> <span class="note">({{ $t("booking.interestin_help") }}) </span>
          </p>
          <el-checkbox-group v-model="formData.interests">
            <el-checkbox :label="1">{{ $t("booking.interests_1") }}</el-checkbox>
            <el-checkbox :label="2">{{ $t("booking.interests_2") }}</el-checkbox>
            <el-checkbox :label="3">{{ $t("booking.interests_3") }}</el-checkbox>
          </el-checkbox-group>
        </el-form-item>
        <el-form-item prop="notes">
          <p class="title">{{ $t('booking.demo_comment') }}</p>
          <el-input
            v-model="formData.notes"
            clearable
            size="medium"
            type="textarea"
            show-word-limit
            maxlength="100"
            :placeholder="$t('booking.optional')"
            :aria-label="$t('booking.demo_comment')"
          ></el-input>
        </el-form-item>
      </el-form>
      <div class="footer">
        <el-button class="submit-btn" :disabled="!isFormValid" @click="handleSubmit()" ><span style="color: #FFF; font-size: 15px; font-weight: 500;">{{ $t("booking.submit") }}</span>
        </el-button>
      </div>
    </div>
    <div class="contact-content" v-else>
       <div class="success-row">
        <img src="@/assets/svg/done.svg" alt="done"/>
        <p class="title">{{ $t('booking.success_title') }}</p>
        <p class="subtitle">{{ $t('booking.success_subtitle') }}</p>
      </div>
      <div class="success-item">
        <div class="booking-info">
          <p class="title">{{ $t('booking.booking_info_title') }}</p>
          <p>• {{ $t('booking.booking_contact_desc1') }}</p>
          <p>• {{ $t('booking.booking_contact_desc2') }}</p> 
          <p>• {{ $t('booking.booking_contact_desc3') }}</p> 
        </div>
      </div>
      <div class="footer">
        <el-button class="submit-btn outlined" @click="handleClear()" ><span style="font-size: 15px; font-weight: 500; color: #1890FF">{{ $t("booking.visit_our_website") }}</span>
        </el-button>
      </div>
    </div>
  </div>
</template>

<script>
import { getAreaCode, getCountryCode, isRealVisitor } from "@/assets/js/utility.js";
import { bookDemo } from "@/api/userApi.js";

export default ({
  data() {
    return {
      loading: false,
      formData: {
        companyName: null,
        fullName: null,
        email: null,
        mobile: null,
        areacode: '+61',
        interests: [],
        notes: null
      },
      areaCodeList: [],
      userAreacode: '',
      rules: {
        companyName: [
          { required: true, message: 'Company name is required', trigger: 'blur' }
        ],
        fullName: [
          { required: true, message: 'Contact Name is required', trigger: 'blur' }
        ],
        email: [
          { required: true, message: 'Email is required', trigger: 'blur' },
          { type: 'email', message: 'Invalid email address', trigger: ['blur', 'change'] }
        ],
        mobile: [
          { required: true, message: 'Mobile is required', trigger: 'blur' },
          { validator: this.validateMobile, message: '', trigger: ['blur', 'change'] }
        ],
        interests: [
          { required: true, message: 'Interest is required', type: 'array', trigger: 'change' }
        ]
      },
      fieldStatus: {}, 
      isSubmit: false,
    }
  },
  mounted() {
    this.handleGetCountryCode();
  },
  computed: {
    isFormValid() {
      const required = Object.keys(this.rules);
      return required.every(key => this.fieldStatus[key] === true);
    }
  },
  methods: {
    validateMobile(rule, value, callback) {
      if (!value) {
        return;
      }
      const phoneRegex = /^(\+?\d{8,15})$/;

      if (!phoneRegex.test(value)) {
        callback(new Error('Invalid mobile number'));
        return;
      }
      callback();
    },
    async handleGetCountryCode() {
      this.areaCodeList = await getAreaCode();

      let currentLocation = localStorage.getItem('currentCountryCode')
      if (!currentLocation) {
        currentLocation = await getCountryCode();
        localStorage.setItem('currentCountryCode', currentLocation)        
      }
      const currentAreacode = (this.areaCodeList || []).find((x) => x.code == currentLocation);

      if (currentAreacode) {
        this.formData.areacode = currentAreacode.areacode;
        this.userAreacode = currentAreacode.areacode;
      }
    },
    handleValidate(prop, isValid) {
      this.$set(this.fieldStatus, prop, isValid);
    },
    async handleSubmit() {
      const valid = await this.$refs.form.validate().catch(() => false);
      if (!valid) return;

      const params = {
        company: this.formData.companyName,
        name: this.formData.fullName,
        areacode: this.formData.areacode,
        mobilenumber: this.formData.mobile,
        email: this.formData.email,
        interests: this.formData.interests.join(","),
        notes: this.formData.notes || '',
        resource: this.getResource()
      }
      this.loading = true;
      try {
        const res = await bookDemo(params);
        if (res.data.success) {
          if (isRealVisitor()) {
            if (window.dataLayer) {
              window.dataLayer.push({ event: 'demo_booking_submitted', resource: params.resource || 'direct' });
            }
            if (typeof window.gtag === 'function') {
              window.gtag('event', 'generate_lead', { resource: params.resource || 'direct', form: 'contact_form' });
            }
          }
          this.$nextTick(() => {
            this.$refs.form.clearValidate();
          });
          this.handleClear();
          this.fieldStatus = {};
          this.isSubmit = true;
          this.loading = false;
        } else {
          this.loading = false;
          this.$message.error(res.data.data || this.$t('err_msg_booking_failed'));
        }
      } catch (err) {
        console.error(err);  
        this.$message.error(this.$t('err_msg_booking_failed')); 
        this.loading = false;     
      }
    },
    handleClear() {
      this.formData.companyName = null;
      this.formData.fullName = null;
      this.formData.email = null;
      this.formData.mobile = null;
      this.formData.areacode = this.userAreacode || '+61';
      this.formData.interests = [];
      this.formData.notes = null;
      this.isSubmit = false;
    },
    getResource() {
      if (this.$route.fullPath.includes('/FB')) {
        return 'Facebook';
      } else {
        return null;
      }
    }
  }
});
</script>

<style lang="scss" scoped>
.contact-form {
  width: 1200px;
  display: flex;
  padding: 32px 30px;
  border-radius: 20px;
  background: #FFF;
  box-shadow: 0 0 16px 0 rgba(24, 144, 255, 0.20);
  .contact-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    width: 100%;
  }
  .header-section {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;

    color: #314455;
    font-size: 16px;
    font-weight: 400;
    line-height: 26px;
    .title {
      font-family: Poppins-Bold;
      color: #062440;
      font-size: 26px;
      line-height: 32px;
    }

  }
  .form-section {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
    .title {
      display: flex;
      gap: 3px;
      color: #606266;
      font-size: 14px;
      font-weight: 500;
      text-align: left;
      padding-bottom: 5px;
      .required {
        color: red;
      }
      .note {
        color: #A8ABB2;
        font-size: 11px;
      }
    }
    .item-row {
      display: flex;
      align-items: flex-start;
      gap: 8px;
    }
  }
  .el-checkbox-group {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0px;
    .el-checkbox {
      padding: 2px;      
      .el-checkbox__input.is-checked+.el-checkbox__label {
        color: #515151 !important;
        font-size: 14px;
        font-weight: 500;
        line-height: normal;
      }      
      .el-checkbox__inner {
        width: 19px;
        height: 19px;
      }
      .el-checkbox__inner::after {
        width: 5px;
        height: 10px;
        left:6px;
      }
    }
  }
  .el-form-item {
    width: 100%;
  }
  .submit-btn {
    width: fit-content;
    border-radius: 5px;
    height: 50px;
    background-color: #1890FF;
  }  
  .submit-btn.is-disabled {
    opacity: 0.4;
  }
  .outlined {
    border: 1px solid #1890FF;
    background-color: white;
  }
  .success-row {
    >img {
      width: 60px;
      height: 60px;
    }
    .title {
      font-size: 18px;
      font-weight: 600;
      line-height: 20px;
      color: #0F0F0F;
      padding-top: 10px;
    }
    .subtitle {
      color: #606266;
      font-size: 13px;
      font-weight: 400;
      line-height: 20px;
      padding-top: 5px;
    }      
  }
  .success-item {
    text-align: left;
    padding: 20px;
    background-color: #F5F7FA;   
    border-radius: 4px;
    width: 100%;
    .booking-info {   
      display: flex;
      flex-direction: column;
      gap: 4px;
      .title {          
        font-weight: 500;
        font-size: 14px;
        color: #0F0F0F;
      }
      >p {
        font-weight: 400;
        font-size: 14px;
        color: #606266;
        line-height: 22px;
      }
    }
  }

  @media screen and ( max-width: 768px ) {
    .contact-content {
      text-align: center;
      .subtitle {
        line-height: 20px;
      }
    }
    .form-section {
      .subtitle {
        flex-direction: column;
        gap:5px;
      }
    }    
  }
}
@media screen and ( max-width: 1220px ) and (min-width: 769px) {
  .contact-form {
    width: 100%;
    margin: auto 26px;
  }
}

.bubble-loader {
  position: fixed;       
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;   
  background: rgba(0,0,0,0.4);
  z-index: 9999;        
  gap: 8px;
}
.bubble-loader span {
  width: 12px;
  height: 12px;
  background: white;   /* change color here */
  border-radius: 50%;
  display: inline-block;
  animation: bounce 0.6s infinite alternate;
}
.bubble-loader span:nth-child(2) {
  animation-delay: 0.2s;
}
.bubble-loader span:nth-child(3) {
  animation-delay: 0.4s;
}
@keyframes bounce {
  from {
    transform: translateY(0);
    opacity: 0.6;
  }
  to {
    transform: translateY(-8px);
    opacity: 1;
  }
}
</style>

<style lang="scss">
.contact-form {
  .custom-select {
    width: 100px;
  }
  @media screen and ( max-width: 768px ) {
    .custom-select {
      width: 120px;
    }    
  }
  .el-input__inner {
    background-color: #F5F7FA !important; 
    border: none;
    border-radius: 4px;
    padding-right: 20px !important;
  }
  .el-input__inner:focus {
    border: 2px solid #1890FF;
    background-color: #1890FF1A !important;
  }
  .el-textarea__inner {
    background-color: #F5F7FA !important; 
    border: none;
    font-size: 14px;
  }
  .el-textarea__inner:focus {
    border: 2px solid #1890FF;
    background-color: #1890FF1A  !important;
  } 
}
.el-select-dropdown__item {
  display: flex;
  align-items: center;
}
</style>

